import { Col, Form, Row, Input, Button, Collapse, Upload, message, Spin } from "antd";
import { GET_ITEMS_CHECK, UPLOAD_BOM_ITEMS } from "../../../../apiServices/API_ENDPOINTS";
import { postData } from "../../../../apiServices/common";
import { useState } from "react";
import { hasPermission, isArrayAndHasValue } from "../../../../utils/functions";
import BulkTable from "./BulkTable";
import { LoadingOutlined, PaperClipOutlined, UploadOutlined } from "@ant-design/icons";
import { ITEM_CREATE_PERMISSION } from "routes/permissions";

const mapCategoryUnits = (categoryUnits = []) =>
  categoryUnits
    .map((unitMap) => ({
      id: unitMap?.unit?.id ?? unitMap?.unit_id,
      name: unitMap?.unit?.name,
      base_unit: unitMap?.unit?.base_unit,
    }))
    .filter((unit) => unit.id);

const getDefaultUnit = (assignedUnits) => {
  if (!isArrayAndHasValue(assignedUnits)) return null;

  const baseUnit = assignedUnits.find((unit) => unit?.base_unit === "Yes");
  return baseUnit || assignedUnits[0];
};

const getEmbeddedCategoryUnits = (item) =>
  item?.item_category_info?.item_category_units ?? item?.item_category_info?.itemCategoryUnits;

const applyAssignedUnitsToItem = (item) => {
  if (item?.assigned_unit_list !== undefined) {
    return item;
  }

  const embeddedCategoryUnits = getEmbeddedCategoryUnits(item);
  if (embeddedCategoryUnits === undefined) {
    return item;
  }

  const assignedUnits = mapCategoryUnits(embeddedCategoryUnits);
  const defaultUnit = getDefaultUnit(assignedUnits);
  const unitInfo = item?.unit_info?.id
    ? item.unit_info
    : defaultUnit
      ? { id: defaultUnit.id, name: defaultUnit.name }
      : null;

  return {
    ...item,
    assigned_unit_list: assignedUnits,
    unit_info: unitInfo,
    unit_id: unitInfo?.id ?? item?.unit_id ?? null,
  };
};

const finalizeItemUnits = (item) => {
  const itemWithEmbeddedUnits = applyAssignedUnitsToItem(item);

  if (itemWithEmbeddedUnits.assigned_unit_list !== undefined) {
    return itemWithEmbeddedUnits;
  }

  return {
    ...itemWithEmbeddedUnits,
    assigned_unit_list: [],
  };
};

const populateItemsWithUnits = (items) => {
  if (!isArrayAndHasValue(items)) return items;
  return items.map((item) => finalizeItemUnits(item));
};

const mapFoundItemFromResponse = (item) =>
  finalizeItemUnits({
    item_info: {
      id: item?.id,
      name: item?.name,
    },
    id: item?.id,
    item_category_id: item?.category_id,
    item_category_info: item?.category,
    item_id: item?.id,
    isAdd: true,
  });

const BulkItemsManage = (props) => {
  const {
    activeBulkKey,
    setActiveBulkKey,
    unitList,
    supplierList,
    foundItems,
    setFoundItems,
    addBulkItems,
    subStyleList,
    drawerType,
    editBulkItems,
  } = props;

  const baseUrl = process.env.REACT_APP_BASE_URL;

  const [notFoundItems, setNotFoundItems] = useState(null);
  const [showTable, setShowTable] = useState(drawerType === "bulk_edit" ? true : false);
  const api_base_url = process.env.REACT_APP_BASE_URL;
  const token = localStorage.getItem("token");
  const [refresh, setRefresh] = useState(0);
  const [loading, setLoading] = useState(false);
  const [csvImportLoading, setCsvImportLoading] = useState(false);
  const [itemsLoading, setItemsLoading] = useState(false);

  const [bulkForm] = Form.useForm();
  const { TextArea } = Input;
  const { Panel } = Collapse;

  const onSubmitText = async (values) => {
    if (!values?.names) return;

    const splittedText = values?.names?.split("\n");
    const bodyData = {
      names: splittedText,
    };
    const query = `${GET_ITEMS_CHECK}`;

    try {
      setItemsLoading(true);
      const response = await postData(query, bodyData);

      if (response && response?.code === 200) {
        const itemsNotFound = isArrayAndHasValue(response?.data?.not_found_data) ? response?.data?.not_found_data : null;
        const foundItems =
          isArrayAndHasValue(response?.data?.found_data) &&
          response?.data?.found_data?.map((item) => mapFoundItemFromResponse(item));
        setFoundItems(foundItems);
        setNotFoundItems(itemsNotFound);

        if (!itemsNotFound) {
          setActiveBulkKey(null);
          setShowTable(true);
        }
      }
    } finally {
      setItemsLoading(false);
    }
  };

  const NotFoundError = ({ notFoundItems, foundItems }) => {
    if (!isArrayAndHasValue(notFoundItems)) return null;

    const itemsName = notFoundItems?.map((item) => item?.name);
    const asString = itemsName?.join(", ");

    return (
      <span>
        <span style={{ color: "red" }}>{asString}</span> items or item unit not found.
        <span>{isArrayAndHasValue(foundItems) ? " Proceed to add?" : " Please change values."}</span>
      </span>
    );
  };

  const proceedAddTable = () => {
    setFoundItems(populateItemsWithUnits(foundItems));
    setActiveBulkKey(null);
    setShowTable(true);
  };

  const uploadProps = {
    accept: ".xlsx",
    headers: {
      authorization: `Bearer ${token}`,
    },
    action: api_base_url + `/api/items/import`,
    onChange(action) {
      if (action.file.status !== "uploading") {
        console.log("uploading file");
      }
      console.log("action.file", action.file);
      if (action.file.status === "done" && action.file.response.code === 200) {
        message.success(`${action.file.response.message}`);
      } else if (action.file.status === "done" && action?.file?.response.code !== 200) {
        message.error(`Items Cannot Be Imported. Category or Attributes doesnot Exist`);
      } else if (action.file.status === "error") {
        message.error(`file upload failed.`);
      }
      setRefresh(refresh);
    },
  };

  const importFromCSV = async (file) => {
    if (!file) {
      message.error("Please select a CSV file");
      return;
    }

    if (!file.name.toLowerCase().endsWith(".csv")) {
      message.error("Please select a valid CSV file");
      return;
    }

    try {
      setCsvImportLoading(true);
      const formData = new FormData();
      formData.append("csv_file", file);

      const response = await postData(UPLOAD_BOM_ITEMS, formData);

      if (response && response.status === "success") {
        message.success("CSV imported successfully");

        const masterData = response.data;

        if (isArrayAndHasValue(masterData)) {
          const mappedItems = masterData.map((item) => {
            let colorInfo = item?.color_info || null;
            let subStyleInfo = item?.sub_style_info || null;

            if (subStyleInfo && colorInfo) {
              colorInfo = {
                ...colorInfo,
                sub_style_id: subStyleInfo?.id || null,
              };
            } else if (subStyleInfo && !colorInfo && subStyleInfo?.color_info) {
              colorInfo = {
                ...subStyleInfo.color_info,
                sub_style_id: subStyleInfo?.id || null,
              };
            }

            return {
              id: item?.item_info?.id || null,
              item_id: item?.item_info?.id || null,
              item_category_id: item?.item_category_info?.id || null,
              item_category_info: item?.item_category_info || null,
              item_info: item?.item_info || null,
              unit_info: item?.unit_info || null,
              order_quantity: item?.order_quantity || null,
              per_product_required_quantity: item?.yy || null,
              extra_qty: null,
              suppliers: item?.suppliers || [],
              color_info: colorInfo,
              sub_style_info: subStyleInfo,
              sub_style_id: subStyleInfo?.id || null,
              color_id: colorInfo?.id || null,
              convert_unit_info: item?.convert_unit_info || null,
              convert_unit_id: item?.convert_unit_info?.id || null,
              converted_qty: item?.convert_qty || null,
              required_quantity: item?.req_qty || null,
              isAdd: true,
            };
          });

          setFoundItems(populateItemsWithUnits(mappedItems));
          setActiveBulkKey(null);
          setShowTable(true);
        } else {
          message.warning("No items found in the uploaded file");
        }
      } else {
        message.error(response?.message || "Failed to import CSV");
      }
    } catch (error) {
      console.error("Error importing CSV:", error);
      message.error("Error occurred while importing CSV");
    } finally {
      setCsvImportLoading(false);
    }
  };

  return (
    <>
      <div style={{ display: "flex", gap: "8px", marginBottom: "20px" }}>
        <Upload {...uploadProps} listType="picture-circle" style={{ flex: 1 }}>
          <Button
            type="success"
            size="small"
            style={{ width: "100%" }}
            icon={<UploadOutlined />}
            loading={loading}
            disabled={!hasPermission([ITEM_CREATE_PERMISSION])}
          >
            {hasPermission([ITEM_CREATE_PERMISSION]) ? "Import" : "No Permission"}
          </Button>
        </Upload>

        <div style={{ flex: 1 }}>
          <input
            type="file"
            id="file-input-bom"
            hidden
            accept=".csv"
            onChange={(e) => importFromCSV(e.target.files[0])}
            onClick={(e) => {
              e.target.value = null;
            }}
          />
          <Button
            type="primary"
            size="small"
            icon={csvImportLoading ? <LoadingOutlined style={{ fontSize: 16 }} spin /> : <UploadOutlined />}
            onClick={() => document.getElementById("file-input-bom").click()}
          >
            Upload BOM Items
          </Button>
          <a
            href={`${baseUrl}/${`storage/CsvUpload/template_bom_detail_items.csv`}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ fontSize: "16px", color: "#1890ff", marginLeft: "10px" }}
          >
            <PaperClipOutlined /> Download Template
          </a>
        </div>
      </div>
      <Collapse
        style={{ marginBottom: 10 }}
        activeKey={activeBulkKey}
        onChange={() => {
          setActiveBulkKey(activeBulkKey ? null : 1);
        }}
      >
        <Panel header={<b>Paste Data</b>} key={1} hidden={drawerType === "bulk_edit"}>
          <>
            <Form form={bulkForm} onFinish={onSubmitText}>
              <Row gutter={6}>
                <Col span={24}>
                  <Form.Item
                    name="names"
                    rules={[
                      {
                        required: true,
                        message: "Please paste texts from your file",
                      },
                    ]}
                  >
                    <TextArea rows={10} placeholder="Paste text here" style={{ width: 1100 }} />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <div className="centered-item-parent">
                    <Button type="primary" size="small" style={{ width: 150 }} htmlType="submit">
                      Add
                    </Button>
                  </div>
                </Col>
              </Row>
            </Form>
            <div style={{ paddingTop: 20, paddingBottom: 20 }}>
              <NotFoundError notFoundItems={notFoundItems} foundItems={foundItems} />
            </div>
            {isArrayAndHasValue(notFoundItems) && isArrayAndHasValue(foundItems) && (
              <Col span={24}>
                <div className="centered-item-parent">
                  <Button type="primary" size="small" style={{ width: 150 }} onClick={proceedAddTable}>
                    Proceed To Add
                  </Button>
                </div>
              </Col>
            )}
          </>
        </Panel>
      </Collapse>

      {showTable && (
        <Spin spinning={csvImportLoading || itemsLoading}>
          <div>
            <BulkTable
              foundItems={foundItems}
              setFoundItems={setFoundItems}
              unitList={unitList}
              supplierList={supplierList}
              addBulkItems={addBulkItems}
              subStyleList={subStyleList}
              drawerType={drawerType}
              editBulkItems={editBulkItems}
            />
          </div>
        </Spin>
      )}
    </>
  );
};

export default BulkItemsManage;
